import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Grid } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { IState } from "store/store.interface";
import { Button, Dialog, Box, Card, Text, Tooltip, Link } from "common";
import { IFormDetails, TBundleFieldType } from "store/reducers/bundlePromotion.reducers";
import Field from "./FieldRenderer";
import { useApiInfo } from "hooks";
import { setLocation } from "store/actions/auth.actions";
import { createBundleCampiagn, clearCreationBundleForm, getPosInfoResource } from 'store/actions/bundlePromotion.actions'
import { useStyles } from './CartBundleForm.style'
import { BUNDLE_PROMOTION_CREATE } from 'store/store.types'
import { clearApi } from "store/actions/api.actions";
import ApprovalBundleScreen from '../ApprovalBundleScreen';
import Loader from 'Loader/Form';


interface IProps {
  onValidateInputs: (error: any) => void;
  registerField: (field: any) => void;
  getValue: (fieldName: string) => string | Date;
  fieldsErrors?: any;
  unRegisterField: (id: string) => void;
  canSubmit: boolean,
}


const CartBundleForm: React.FC<IProps> = ({
  onValidateInputs,
  registerField,
  getValue,
  unRegisterField,
  fieldsErrors,
  canSubmit,
}) => {

  const classes = useStyles();

  const { formDetails } = useSelector((state: IState) => state.bundlePromotion);
  const [openApproval, setOpenApproval] = useState(false);
  const [btnText, setBtnText] = useState('')
  const { isLoading = false, error, done: isCreationDone = false } = useApiInfo(BUNDLE_PROMOTION_CREATE);

  const downloadFields = ["posId","includedPosId","excludedPosId"]

  const dispatch = useDispatch();

  const onCloseNotification = () => {
    dispatch(clearApi(BUNDLE_PROMOTION_CREATE));
  };

  const updateApprovalScreen = (status: boolean) => () => {
    setOpenApproval(status);
    onCloseNotification();
  };

  const onSubmit = () => {
    if (btnText === 'cancel') {
      dispatch(clearCreationBundleForm());
      dispatch(setLocation("/cart-bundles"));

    } else {
      dispatch(clearApi(BUNDLE_PROMOTION_CREATE));
      dispatch(createBundleCampiagn())
    }
  };


  const confirmationHandle = (btnText: string) => {
    if (btnText === 'submit') {
      setOpenApproval(true);
      setBtnText('submit')
    } else {
      setOpenApproval(true);
      setBtnText('cancel')
    }
  }

  const isFieldDisabled = useCallback((name: string) => {
    return name === 'discountQuantity';
  }, []);

  const downloadPosInfoTemplateHandler = () => {
    dispatch(getPosInfoResource());
  }

  const {
    fields = [],
  } = useMemo(() => formDetails || ({} as IFormDetails), [formDetails]);


  const renderField = useCallback(
    (field: TBundleFieldType, section: "cart-bundle") => {
      const { name } = field;
      const { isError: fieldError, message } = fieldsErrors?.[name] || {}

      return (
        <Field
          disable={isFieldDisabled(name)}
          field={field}
          onValidateInput={onValidateInputs}
          getValue={getValue}
          error={fieldError}
          errorMessage={message}
          testId={section}
          registerField={registerField}
          unRegisterField={unRegisterField}
          variant='outlined'
        />
      );
    },
    [
      getValue,
      onValidateInputs,
      registerField,
      fieldsErrors,
      unRegisterField,
      isFieldDisabled
    ]
  );

  const isDiscountfieldRequired = (name: string) => {
    return name === 'bundleOffering' && getValue('bundleOffering') === 'OFF ON 2ND UNIT'
  }

  useEffect(() => {
    if (!isLoading && !error && isCreationDone) {
      dispatch(setLocation("/cart-bundles"));
    }
  }, [isCreationDone, isLoading, error, dispatch]);

  if (!formDetails) {
    return <Loader />;
  }

  return (
    <Card padding="30px">
      <Dialog
        open={openApproval}
        onBackdropClick={updateApprovalScreen(false)}
        onClose={updateApprovalScreen(false)}
        dialogTitle={
          <Text className={classes.dialogText}>
            {
              btnText === 'submit' ? 'Are you sure you want to create this campaign?'
                : 'Are you sure you want to discard the changes?'
            }
          </Text>
        }
      >
        <ApprovalBundleScreen
          updateApprovalScreen={updateApprovalScreen(false)}
          onCloseNotification={onCloseNotification}
          onSubmit={onSubmit}
          isLoading={isLoading}
          error={error}
        />
      </Dialog>
      <Grid container spacing={2}>
        {fields.map((field) => {
          if (field.name === "discountValue") return null;
          return <Grid
            key={field.name}
            item
            xs={6}
            style={{ marginTop: 0 }}
            sm={field.isSingle ? 12 : 6}
          >
            <Box key={field.name}
              className={field.type === 'dateRange' ? classes.dateSeparator : undefined}
            >
              {field.description ? (
                <Box mb={1} display='flex' justifyContent='space-between'>
                  <Tooltip
                    isIconOnly={true}
                    title={field.description}
                    withHelpIcon
                  >
                    <Text >{field.label}</Text>
                  </Tooltip>
                  {downloadFields.includes(field.name) && <Link
                    data-testid='back-btn'
                    variant="default"
                    color="primary"
                    onClick={downloadPosInfoTemplateHandler}
                    marginRight="15px"
                  >
                    Download sample template
                  </Link>}
                </Box>
              ) : (
                <Text mb={1}>{field.label}</Text>
              )}
              <Box display={isDiscountfieldRequired(field.name) && 'flex'}>
                <Box display={isDiscountfieldRequired(field.name) && 'inline-block'}>{renderField(field, 'cart-bundle')}</Box>
                {isDiscountfieldRequired(field.name) && (
                  <Box display='flex'>
                    {renderField(
                      fields.find((fields) => fields.name === 'discountValue') as TBundleFieldType,
                      'cart-bundle'
                    )}
                    <Text mt={2} ml={1}>%</Text>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        })}
      </Grid>
      <Box display='flex' mt={2}>
        <Button
          variant="contained"
          color="primary"
          data-testid='submit-btn'
          style={{ marginRight: 16 }}
          onClick={() => confirmationHandle('submit')}
          disabled={!canSubmit}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          data-testid='cancel-btn'
          onClick={() => confirmationHandle('cancel')}
        >
          Cancel
        </Button>
      </Box>
    </Card>
  );
};

export default CartBundleForm